import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";

import { CaseSlide } from "pwapi";

import { OpenAPI } from "pwapi/core/OpenAPI";

import {
  fetchSlideClassification,
  fetchSpecimenClassification,
  wipeAI,
} from "shared/actions/aiActions";
import { replaceChargeCaches } from "shared/actions/caseDetailActions";
import {
  fetchCaseByAccessionNumber,
  slideDetail,
  wipeSlideDetail,
} from "shared/actions/slideDetailActions";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import { slideLabel } from "shared/utils/slideUtils";

import ErrorPane from "components/ErrorPane";
import PageLayout from "components/PageLayout";
import Spinner from "components/Spinner";
import ToastList from "components/toasts/ToastList";
import {
  getMostRecentScreenRequest,
  slideClassification,
} from "components/viewer/dialogs/utils";
import SlideViewer from "components/viewer/SlideViewer";
import { useHasAILoaded, useIsAILoading } from "hooks/aiHooks";
import useViewerBarcodeScanner from "hooks/useViewerBarcodeScanner";
import useViewerItems, { ViewerItem } from "hooks/useViewerItems";
import { selectAutoCptCodesEnabled } from "selectors/autoCptCodesEnabled";
import { selectSlideClassification } from "selectors/classificationSelector";
import { useAppDispatch, useAppSelector } from "store/hooks";

import FourOhFourPage from "./FourOhFourPage";

const { REACT_APP_WS_HOST, REACT_APP_WS_PORT, REACT_APP_WS_PROTOCOL } =
  process.env;

const webSocketBaseUrl = `${REACT_APP_WS_PROTOCOL}://${REACT_APP_WS_HOST}:${REACT_APP_WS_PORT}/ws`;

const getSlideItems = (vItems: ViewerItem[]) =>
  vItems.filter(({ type }) => type === "slides");

const createGetSlideItem =
  (shouldFavorUnviewedSlides: boolean) =>
  (slideItems: ViewerItem[], slides: CaseSlide[] = []) => {
    const freshSlides = slides.filter(
      ({ was_previously_viewed }) => !was_previously_viewed
    );

    // If option to favor Unviewed Slides is set to `false`, return first slideItem
    if (!shouldFavorUnviewedSlides) return slideItems.at(0);

    // If no Fresh Slides, return first slideItem
    if (!freshSlides.length) return slideItems.at(0);

    // Return first encountered Fresh slide item
    return slideItems.find((i) => freshSlides.some((s) => s.id === i.id));
  };

// Finds the first slide which matches any specimen in the screen request
// Since the viewerItems list is sorted by specimen and slide,
// the first viewer item that matches will be the first slide of the first specimen of the screen request.
const getScreenRequestItem = (
  slideItems: ViewerItem[],
  specimenIds: number[] = []
): ViewerItem | undefined =>
  slideItems.find(
    ({ specimenId }) => !!specimenId && specimenIds.includes(specimenId)
  ) || slideItems.at(0);

const useInitViewerItemIds = () => {
  let {
    case_id = "",
    slide_id = "",
    document_id = "",
    specimen_id = "",
  } = useParams<{
    case_id: string;
    slide_id: string;
    document_id: string;
    specimen_id: string;
  }>();

  // If case_id isn't all digits, it's to be considered an accession ID
  let accession_id = "";
  if (!case_id.match(/^\d+$/)) {
    accession_id = case_id;
    case_id = "";
  }

  const caseId = case_id ? parseInt(case_id) : undefined;
  const slideId = slide_id ? parseInt(slide_id) : undefined;
  const documentId = document_id ? parseInt(document_id) : undefined;
  const specimenId = specimen_id ? parseInt(specimen_id) : undefined;

  return { accession_id, caseId, slideId, documentId, specimenId };
};

const SlideViewerPage = (_props: any) => {
  const authObject = useAppSelector(({ auth }) => auth);
  const sysObject = useAppSelector(({ sys }) => sys);
  const caseDetailObject = useAppSelector(({ caseDetail }) => caseDetail);
  const slideDetailObject = useAppSelector(({ slideDetail }) => slideDetail);
  const autoCptCodesEnabled = useAppSelector(selectAutoCptCodesEnabled);

  const shouldFavorUnviewedSlides = useAppSelector((state) =>
    selectFeatureFlag(state, "shouldFavorUnviewedSlides")
  );
  const chargeCacheSocketsFlag = useAppSelector((state) =>
    selectFeatureFlag(state, "chargeCacheSockets")
  );
  const getSlideItem = createGetSlideItem(shouldFavorUnviewedSlides);

  const aiObject = useAppSelector((state) =>
    selectSlideClassification(state, slideDetailObject?.id)
  );

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams] = useSearchParams();
  const [sourceQuery, setSourceQuery] = useState<string | null>(null);
  useEffect(() => {
    setSourceQuery(searchParams.get("source"));
  }, [caseDetailObject?.id]);

  const error: Error | undefined =
    caseDetailObject.error || slideDetailObject.error;

  const viewerItems = useViewerItems();
  const screenRequest = getMostRecentScreenRequest(
    caseDetailObject.screen_requests
  );

  const { accession_id, caseId, slideId, documentId, specimenId } =
    useInitViewerItemIds();

  const getViewerItem = (): ViewerItem | undefined => {
    // If itemId is specified, return it
    if (!!slideId) return { id: slideId, type: "slides" };
    if (!!documentId) return { id: documentId, type: "documents" };
    if (!!specimenId) return { id: specimenId, type: "specimens" };

    // Look for Slides.  If none, return first ViewerItem
    const slideItems = getSlideItems(viewerItems);
    if (!slideItems.length) return viewerItems.at(0);

    // If Screeen Request, return requested item
    const { slides, is_screening } = caseDetailObject;
    if (!!screenRequest && is_screening)
      return getScreenRequestItem(slideItems, screenRequest.specimens);

    // Return Slide item.
    return getSlideItem(slideItems, slides);
  };

  const viewerItem = getViewerItem();

  const hasOrderedDocuments = caseDetailObject.documents.some(
    (d) => d.status === "uploaded" && d.lab_order_id
  );

  // Moving all these "wipe" actions to this one ∏useEffect call where they won't wreak havoc.
  useEffect(() => {
    // Runs once when mounting
    dispatch(wipeSlideDetail());
    dispatch(wipeAI()); // TODO: "WIPE AI" is a code smell
    return () => {
      // Runs once on unmounting, in reversed order for clarity
      dispatch(wipeAI()); // TODO: "WIPE AI" is a code smell
      dispatch(wipeSlideDetail());
    };
  }, [dispatch]);

  useEffect(() => {
    if (viewerItem?.id && viewerItem?.type === "slides") {
      dispatch(slideDetail(viewerItem.id));
    }
  }, [viewerItem?.id, viewerItem?.type]);

  useEffect(() => {
    if ((documentId || specimenId) && !slideId) {
      dispatch(wipeSlideDetail());
      dispatch(wipeAI()); // TODO: "WIPE AI" is a code smell
    }
  }, [slideId, documentId, specimenId]);

  useEffect(() => {
    if (!caseId && slideDetailObject.case_id) {
      navigate(
        `/cases/${slideDetailObject.case_id}/slides/${slideDetailObject.id}`,
        { replace: true }
      );
    } else if (!caseId && slideDetailObject.caseByAccessionResult) {
      // The case id is changing here which causes the search params to drop off.
      // Need to keep the search params to preserve the source query
      navigate(
        {
          pathname: `/cases/${slideDetailObject.caseByAccessionResult.id}`,
          ...(searchParams.toString() && { search: searchParams.toString() }),
        },
        { replace: true }
      );
    }
  }, [
    caseId,
    slideDetailObject.caseByAccessionResult,
    // props.history,
    // Inclusion of slideDetailObject.case_id triggers infinite loop
    slideDetailObject.case_id,
    slideDetailObject.id,
  ]);

  useEffect(() => {
    if (
      caseId &&
      !slideId &&
      !documentId &&
      !!viewerItem?.id &&
      !!viewerItem?.type
    ) {
      navigate(`/cases/${caseId}/${viewerItem?.type}/${viewerItem?.id}`, {
        replace: true,
      });
    }
  }, [
    caseId,
    slideId,
    documentId,
    specimenId,
    viewerItem?.id,
    viewerItem?.type,
  ]);

  useEffect(() => {
    if (accession_id) {
      dispatch(fetchCaseByAccessionNumber(accession_id));
    }
  }, [dispatch, accession_id, authObject.access]);

  const classificationInfo = slideClassification(slideDetailObject);
  const aiLoaded = useHasAILoaded(slideDetailObject);
  const aiLoading = useIsAILoading(slideDetailObject);

  useEffect(() => {
    if (!sysObject.features.prospectiveAI) return;

    if (
      sysObject.features.specimenClassification &&
      caseId &&
      slideDetailObject.specimen?.id &&
      !aiLoaded &&
      !aiLoading
    ) {
      dispatch(
        fetchSpecimenClassification({
          parentLookupCaseId: caseId,
          id: slideDetailObject.specimen?.id,
        })
      );
    } else if (
      classificationInfo?.id &&
      slideDetailObject.id &&
      aiObject?.id !== classificationInfo.id &&
      !aiLoaded &&
      !aiLoading
    ) {
      // Drop back to traditional single-classification output
      dispatch(
        fetchSlideClassification({
          id: classificationInfo.id,
          slide_id: slideDetailObject.id,
        })
      );
    } else if (!classificationInfo?.id) {
      // TODO: this still doesn't belong here; code smell
      dispatch(wipeAI());
    }
  }, [
    sysObject.features.prospectiveAI,
    sysObject.features.specimenClassification,
    caseDetailObject.slides?.length,
    caseId,
    slideDetailObject.specimen?.id,
    classificationInfo?.id,
    slideDetailObject.id,
    aiObject?.id,
    aiLoaded,
    aiLoading,
  ]);

  useEffect(() => {
    dispatch(wipeAI());
  }, [slideDetailObject?.specimen?.id]);

  useEffect(() => {
    if (
      !caseDetailObject?.id ||
      !autoCptCodesEnabled ||
      !chargeCacheSocketsFlag
    )
      return;

    const socket = new WebSocket(
      `${webSocketBaseUrl}/charges/${caseDetailObject?.id}/` +
        (REACT_APP_WS_PROTOCOL !== "wss" && REACT_APP_WS_HOST === "localhost"
          ? `?token=${OpenAPI.TOKEN}`
          : "")
    );

    const onMessage = (message: MessageEvent) => {
      try {
        const event = JSON.parse(message.data);
        caseDetailObject?.id &&
          event?.type === "charges" &&
          dispatch(replaceChargeCaches(caseDetailObject.id, event.data));
      } catch (e) {
        if (e instanceof SyntaxError) {
          console.error("Invalid JSON from SSE: ", message.data);
        } else {
          throw e;
        }
      }
    };

    socket.addEventListener("message", onMessage);

    return () => {
      socket.removeEventListener("message", onMessage);
      socket.close();
    };
  }, [caseDetailObject?.id, autoCptCodesEnabled, chargeCacheSocketsFlag]);

  const specimen = caseDetailObject.specimens?.find((s) => s.id === specimenId);
  const slideName = slideLabel(
    slideDetailObject,
    slideDetailObject.block_obj,
    slideDetailObject.specimen
  );

  const slidelessViewer = useAppSelector((state) =>
    selectFeatureFlag(state, "slidelessViewer")
  );

  useViewerBarcodeScanner((payload) => {
    const { object_type, object_id } = payload;
    if (
      object_type === "slide" &&
      object_id &&
      object_id !== slideId &&
      caseDetailObject?.slides.map((s) => s.id).includes(object_id)
    ) {
      navigate(`/cases/${caseDetailObject?.id}/slides/${object_id}`);
    }
  });

  const helmetTitle = [
    !!slideDetailObject?.id ? `Slide ${slideName} ` : "",
    !!specimen ? `Specimen ${specimen.name} ` : "",
    // !!slideDetailObject?.id || !!specimen ? "- " : "Case ",
    // !!caseDetailObject?.accession ? `${caseDetailObject.accession} ` : "",
    !!slideDetailObject?.id || !!specimen ? "- " : "",
    "Pathology Watch",
  ].join("");

  return (
    <>
      <Helmet>
        <title>{helmetTitle}</title>
      </Helmet>
      <PageLayout>
        <ToastList />
        {slideDetailObject.noMatchingCaseByAccessionResult ? (
          <div className="FullScreenCenter">
            <div>
              Accession {accession_id} is not currently available.
              <br />
              Please contact{" "}
              <a href="mailto:support@pathologywatch.com">
                <strong>support@pathologywatch.com</strong>
              </a>{" "}
              if this is unexpected.
              <br />
              <br />
              <div style={{ marginLeft: "150px", marginRight: "150px" }}>
                <Link to={"/"}>
                  <div className="Button">HOME</div>
                </Link>
              </div>
            </div>
          </div>
        ) : !slideDetailObject.loading &&
          !error &&
          !slideDetailObject.case_id ? (
          <SlideViewer
            authObject={authObject}
            slideDetailObject={slideDetailObject}
            documentId={documentId}
            specimenId={specimenId}
            slideId={slideId}
            aiObject={aiObject}
            sourceQuery={sourceQuery}
          />
        ) : caseDetailObject.loading ? (
          <div className="FullScreenCenter">
            <Spinner />
          </div>
        ) : error ? (
          <div style={{ marginTop: "50px" }}>
            <ErrorPane error={error} />
          </div>
        ) : (!slidelessViewer &&
            !slideId &&
            caseDetailObject.slides.length === 0 &&
            !hasOrderedDocuments) ||
          (!slideDetailObject.loading &&
            caseDetailObject.id !== slideDetailObject.case_id) ? (
          <FourOhFourPage />
        ) : (
          <SlideViewer
            authObject={authObject}
            caseDetailObject={caseDetailObject}
            slideDetailObject={slideDetailObject}
            aiObject={aiObject}
            hasOrderedDocuments={hasOrderedDocuments}
            documentId={documentId}
            specimenId={specimenId}
            slideId={slideId}
            sourceQuery={sourceQuery}
          />
        )}
      </PageLayout>
      <div id="custom-toolbar" style={{ display: "none" }} />
    </>
  );
};

export default SlideViewerPage;
